import Highcharts from 'highcharts';

/**
 * Configure Highcharts to use the given IANA timezone for datetime axis rendering.
 * Highcharts v12+ uses the native `time.timezone` option (IANA string) instead of
 * the removed `time.getTimezoneOffset` callback.
 *
 * Call this once per chart component before building the options object.
 */
export const setHighchartsTimezone = (timezone: string): void => {
  Highcharts.setOptions({
    time: {
      timezone,
    },
  });
};

/**
 * Mobile touch-scroll fix for Highcharts v13.
 *
 * Root cause: Highcharts' Pointer.touch() calls e.preventDefault() on touchstart
 * and touchmove events, which blocks page scrolling. However, we still want the
 * tooltip to follow the finger (Highcharts' internal tracking logic).
 *
 * Fix: Monkey-patch Event.prototype.preventDefault so that when called on touch
 * events whose target is inside a .highcharts-container, it becomes a no-op.
 * This allows Highcharts' touch handlers to run normally (tooltip follows finger)
 * but removes the scroll-blocking side effect.
 *
 * Trade-off: Pinch-to-zoom on charts is disabled on mobile (the browser handles
 * pinch as page zoom instead). This is acceptable since our charts don't use
 * zooming.
 */

/**
 * Touch device detection — defense-in-depth with three independent checks:
 *
 * 1. `'ontouchstart' in window` — Touch Events API is available in this browser.
 * 2. `navigator.maxTouchPoints > 0` — hardware reports touch capability (covers
 *    PointerEvents-only browsers where 'ontouchstart' may be absent).
 * 3. `window.matchMedia('(hover: none)').matches` — the PRIMARY input device has
 *    no hover capability. This is the crucial discriminator that excludes desktop
 *    touchscreen laptops (Surface Pro, etc.) where the primary input is a
 *    mouse/trackpad. On those devices, maxTouchPoints > 0 is true but (hover: none)
 *    is false — without this check, the patch would incorrectly activate.
 *
 * All three together ensure the patch only runs on phones/tablets where touch is
 * the primary interaction and scrolling over charts is the expected gesture.
 */
const isTouchDevice =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
  window.matchMedia('(hover: none)').matches;

if (isTouchDevice && typeof document !== 'undefined') {
  // oxlint-disable-next-line typescript/unbound-method -- intentional: capturing prototype method for monkey-patch, called with correct this via .call()
  const origPreventDefault = Event.prototype.preventDefault;

  Event.prototype.preventDefault = function (this: Event): void {
    // Only intercept touch events targeting Highcharts containers
    if (
      (this.type === 'touchstart' || this.type === 'touchmove') &&
      // Touch targets on Highcharts SVG charts are always Element instances
      // (SVGElement extends Element). If a Text node is somehow the target,
      // we correctly fall through to the original preventDefault — Highcharts'
      // Pointer.touch() only operates on container Elements, not raw text nodes.
      this.target instanceof Element &&
      this.target.closest('.highcharts-container')
    ) {
      // No-op: let the browser handle the gesture (scroll) while Highcharts
      // still processes the event for tooltip/crosshair tracking
      return;
    }
    origPreventDefault.call(this);
  };
}

/**
 * Dismiss ghost tooltips on page scroll (outside: true workaround).
 *
 * When tooltip.outside=true, Highcharts appends a <div class="highcharts-tooltip-container">
 * to document.body. On touch devices, scrolling the page doesn't trigger Highcharts'
 * pointerleave, so the tooltip stays visible after the chart scrolls away.
 * This is a known Highcharts limitation (issues #11635, #20969) with no native fix
 * as of v13.0.0 (July 2026).
 *
 * Note: @highcharts/react v5 (declarative JSX API) does NOT populate the global
 * Highcharts.charts array, so we cannot use chart.pointer.reset(). Instead we
 * manipulate the tooltip container DOM directly.
 *
 * Fix: After 100px of scroll, move all tooltip containers off-screen via CSS
 * transform. This visually hides them without affecting Highcharts' internal
 * visibility/display state. When the user next interacts with a chart, Highcharts
 * repositions the container with inline styles, overriding our transform.
 */
if (typeof window !== 'undefined') {
  let lastScrollY = window.scrollY;

  window.addEventListener(
    'scroll',
    () => {
      const delta = Math.abs(window.scrollY - lastScrollY);
      if (delta < 100) return;

      lastScrollY = window.scrollY;

      const containers = document.querySelectorAll('.highcharts-tooltip-container');
      for (const el of containers) {
        // Move off-screen. Highcharts overwrites `left`/`top` on next tooltip show,
        // which naturally brings it back into view. This avoids desynchronizing
        // Highcharts' internal isHidden/visibility state.
        (el as HTMLElement).style.left = '-9999px';
        (el as HTMLElement).style.top = '-9999px';
      }
    },
    { passive: true }
  );

  /**
   * Clean up orphaned tooltip containers when charts are destroyed.
   * See issue #18490 — tooltip.outside containers aren't always removed on destroy.
   */
  Highcharts.addEvent(Highcharts.Chart, 'destroy', function () {
    // oxlint-disable-next-line typescript/no-explicit-any -- Highcharts internal: tooltip.container is not in public typings
    const tooltipContainer = (this as any).tooltip?.container;
    if (tooltipContainer && tooltipContainer.parentNode) {
      tooltipContainer.parentNode.removeChild(tooltipContainer);
    }
  });
}
